import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const crc = (n) =>
  "CRC " + Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const paymentLabel = (status, orderStatus) => {
  if (orderStatus === "Cancelada" || status === "VOID") return "Sin efecto";
  if (status === "Cancelled") return "Pagada";
  return status || "—";
};

const isIOSDevice = () => {
  if (typeof navigator === "undefined") return false;

  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const deliverPDF = (doc, filename) => {
  if (!isIOSDevice() || typeof window === "undefined" || typeof document === "undefined") {
    doc.save(filename);
    return;
  }

  // Safari on iOS can fail to complete jsPDF's direct save()/Blob download flow.
  // Opening the generated PDF as a document is more reliable and lets the user
  // use Safari's native Share sheet to Save to Files, print or share it.
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.setAttribute("aria-hidden", "true");
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Keep the object URL alive long enough for Safari's PDF viewer to load it.
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export function generateInvoicePDF(bill) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFillColor(27, 77, 62);
  doc.rect(0, 0, pageW, 86, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("LabAroid", 40, 43);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Costa Rica", 40, 62);

  doc.setTextColor(30, 30, 30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("COMPROBANTE DE VENTA", pageW - 40, 116, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Orden: ${bill.bill_number}`, pageW - 40, 134, { align: "right" });
  const dt = new Date(bill.created_at);
  doc.text(`Fecha: ${Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleString("es-CR")}`, pageW - 40, 149, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.text("Cliente:", 40, 116);
  doc.setFont("helvetica", "normal");
  doc.text(bill.customer_name || "Cliente", 40, 134);
  if (bill.customer_phone) doc.text(`Teléfono: ${bill.customer_phone}`, 40, 149);

  doc.setFontSize(9);
  doc.text(`Estado orden: ${bill.order_status || "—"}`, 40, 165);
  doc.text(`Estado pago: ${paymentLabel(bill.payment_status, bill.order_status)}`, 180, 165);
  if (bill.payment_method) doc.text(`Método: ${bill.payment_method}`, 320, 165);

  const rows = bill.items.map((it, i) => [
    i + 1,
    it.name,
    crc(it.price),
    it.quantity,
    crc(it.line_total ?? it.price * it.quantity),
  ]);

  autoTable(doc, {
    startY: 182,
    head: [["#", "Producto", "Precio unitario", "Cant.", "Total"]],
    body: rows,
    theme: "grid",
    headStyles: { fillColor: [27, 77, 62], textColor: 255, halign: "left" },
    columnStyles: {
      0: { halign: "center", cellWidth: 30 },
      2: { halign: "right" },
      3: { halign: "center", cellWidth: 40 },
      4: { halign: "right" },
    },
    styles: { fontSize: 9, cellPadding: 6 },
  });

  let y = doc.lastAutoTable.finalY + 20;
  const rightX = pageW - 40;
  const labelX = pageW - 220;
  const line = (label, val, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 12 : 10);
    doc.text(label, labelX, y);
    doc.text(val, rightX, y, { align: "right" });
    y += bold ? 22 : 18;
  };

  line("Subtotal", crc(bill.subtotal));
  if (Number(bill.discount_amount || 0) > 0) line("Descuento", "- " + crc(bill.discount_amount));
  if (Number(bill.shipping_amount || 0) > 0) line("Envío", crc(bill.shipping_amount));
  if (Number(bill.tax_amount || 0) > 0) line(`IVA (${Number(bill.tax_percent || 0)}%)`, crc(bill.tax_amount));
  doc.setDrawColor(200);
  doc.line(labelX, y - 8, rightX, y - 8);
  line("Total", crc(bill.total), true);

  if (bill.order_status === "Cancelada") {
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(180, 40, 40);
    doc.text("ORDEN CANCELADA", 40, y);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(110);
  doc.text("Documento generado por VerdantPOS a partir del registro operativo de LabAroid.", 40, y + 34);

  deliverPDF(doc, `LabAroid_Order_${bill.order_id || bill.id}.pdf`);
}
